import { useCallback, useEffect, useRef, useState } from "react";
import {
  useAppDispatch,
  useAppNavigate,
  useAppParams,
  useAppSelector,
} from "@app/hooks";
import {
  invalidateProjectAreaStatus,
  selectCurrentProject,
  selectProjectAreaDefined,
} from "@app/features/project/projectSlice";
import { useLazyGetActivitiesByIdQuery } from "@app/hooks/useActivityHooks";
import { useLazyGetProjectByIdQuery } from "@app/features/project/projectApiSlice";
import { IMapService } from "./types/mapService";
import { agroInformaticsMapService } from "./services/agroInformaticsMapService";
import { createBintactMessageHandler } from "./handlers/bintactMessageHandler";
import {
  createOpenMapPayload,
  createSaveAndExitRequest,
  createSetBaseLineRequest,
  SaveAndExitResponse,
  EditingStartedMessage,
  SetBaseLineResponse,
} from "./bintactMapMessages";
import { useHybridLockStatus } from "@app/hooks/useHybridLockStatus";
import { FeatureCollection } from "@components/actions/map/mapMessages";
import { sendMapConfiguration } from "@components/actions/map/mapConfigUtils";
import { triggerMsaCalculation } from "@app/services/msaApiService";
import {
  startMapSavePolling,
  selectMapSaveStatusMessage,
} from "@app/features/mapSaveStatus/mapSaveStatusSlice";

export interface StatusMessage {
  text: string;
  type: "success" | "error" | "info";
  color: string;
  backgroundColor: string;
  borderColor: string;
}

interface UseBintactMapCommunicationOptions {
  mapService?: IMapService;
  initialState?: {
    isFirstMapLoad?: boolean;
    initialMapShape?: FeatureCollection;
  } | null;
  isBaselineMode?: boolean;
  returnPath?: string;
}

const useBintactMapCommunication = (
  options?: UseBintactMapCommunicationOptions,
) => {
  const mapService = options?.mapService ?? agroInformaticsMapService;

  const { project, activity } = useAppSelector(selectCurrentProject);
  const { rawProjectId, rawActivityId } = useAppParams();
  const reduxMapSaveStatus = useAppSelector((state) =>
    rawProjectId ? selectMapSaveStatusMessage(state, rawProjectId) : null,
  );
  const { lockProject, unlockProject } = useHybridLockStatus();
  const projectAreaDefined = useAppSelector(selectProjectAreaDefined);
  const [messages, setMessages] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(
    null,
  );
  const [getActivityById] = useLazyGetActivitiesByIdQuery();
  const [getProjectById] = useLazyGetProjectByIdQuery();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refs to track first load configuration
  const isFirstLoadRef = useRef(options?.initialState?.isFirstMapLoad ?? false);
  const initialShapeRef = useRef(
    options?.initialState?.initialMapShape ?? null,
  );

  // Helper function to set status message with smart auto-clear logic
  const setStatusWithTimeout = useCallback(
    (message: StatusMessage | null, clearAfterMs?: number) => {
      // Clear any existing timeout
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
        statusTimeoutRef.current = null;
      }

      setStatusMessage(message);

      // Set timeout to clear message only for non-error types
      if (message && message.type !== "error") {
        const timeout = clearAfterMs ?? 5000; // Default 5 seconds for success/info
        statusTimeoutRef.current = setTimeout(() => {
          setStatusMessage(null);
          statusTimeoutRef.current = null;
        }, timeout);
      }
      // Errors remain visible until explicitly cleared (no timeout)
    },
    [],
  );

  // Helper function to fetch project name
  const fetchProjectName = useCallback(
    async (projectId: string): Promise<string> => {
      if (project && project.id === Number(projectId)) {
        return project.name ?? "";
      }
      try {
        const projectData = await getProjectById(projectId).unwrap();
        return projectData?.name ?? "";
      } catch (error) {
        console.error("Failed to fetch project:", error);
        return "";
      }
    },
    [project, getProjectById],
  );

  // Helper function to fetch activity name
  const fetchActivityName = useCallback(
    async (activityId: string): Promise<string> => {
      if (activity && activity.id === Number(activityId)) {
        return activity.name ?? "";
      }
      try {
        const activityData = await getActivityById(Number(activityId)).unwrap();
        return activityData?.name ?? "";
      } catch (error) {
        console.error("Failed to fetch activity:", error);
        return "";
      }
    },
    [activity, getActivityById],
  );

  // Handle opening the map with project/activity data
  const handleOpenMapRequest = useCallback(async () => {
    if (!iframeRef.current || !rawProjectId) {
      console.error(
        "Failed to send open map: iframe not initialized or project ID not set",
      );
      return;
    }

    // If this is the first load and we have an initial shape, send configuration first
    if (isFirstLoadRef.current && initialShapeRef.current) {
      setHasUnsavedChanges(true);
      try {
        await sendMapConfiguration(
          iframeRef.current,
          initialShapeRef.current,
          mapService.getMapUrl(),
          project ?? undefined,
          true,
        );

        // Mark as sent to prevent resending
        isFirstLoadRef.current = false;
        initialShapeRef.current = null;
        console.log("PREFIX: Configuration sent successfully");
      } catch (err) {
        console.error("PREFIX: Failed to send initial configuration:", err);
        // Continue with OPEN_MAP even if configuration fails
      }
    }
    // Send OPEN_MAP message (normal flow)
    const projectName = await fetchProjectName(rawProjectId);
    let activityName: string | undefined;

    if (rawActivityId) {
      activityName = await fetchActivityName(rawActivityId);
    }

    const payload = createOpenMapPayload(
      rawProjectId,
      projectName,
      rawActivityId,
      activityName,
      options?.isBaselineMode,
    );

    console.log("PREFIX: Sending open map payload:", payload);
    mapService.sendMessage(iframeRef.current, payload);
  }, [
    iframeRef,
    rawProjectId,
    rawActivityId,
    fetchProjectName,
    fetchActivityName,
    mapService,
    project,
  ]);

  // Handle save request
  const handleSaveRequest = useCallback(() => {
    if (!iframeRef.current) {
      console.error("Cannot save: iframe not initialized");
      return;
    }

    setIsSaving(true);
    setStatusWithTimeout(null); // Clear any existing status messages
    const request = createSaveAndExitRequest();
    mapService.sendMessage(iframeRef.current, request);
  }, [iframeRef, mapService, setStatusWithTimeout]);

  // Handle exit without saving
  const handleExitRequest = useCallback(() => {
    unlockProject();
    // Smart redirect: return to the page we came from, or default to activities
    if (options?.returnPath) {
      // Special case: if coming from /project/new/* path, replace 'new' with actual project ID
      const updatedPath = options.returnPath.replace(
        /\/project\/new\//,
        `/project/${rawProjectId}/`,
      );
      // returnPath is a full pathname that already carries any /public prefix.
      navigate({ path: updatedPath, ignorePublic: true });
    } else if (window.history.length > 1) {
      // Browser has history, go back
      window.history.back();
    } else {
      // No history or return path, default to activities
      navigate({ path: `/project/${rawProjectId}/activities` });
    }
  }, [navigate, rawProjectId, unlockProject, options?.returnPath]);

  // Handle set baseline request
  const handleSetBaselineRequest = useCallback(() => {
    if (!iframeRef.current) {
      console.error("Cannot set baseline: iframe not initialized");
      return;
    }

    console.log("PREFIX: Sending set baseline request");
    setStatusWithTimeout(null); // Clear any existing status messages
    const request = createSetBaseLineRequest();
    mapService.sendMessage(iframeRef.current, request);
  }, [iframeRef, mapService, setStatusWithTimeout]);

  // Handle save response
  const handleSaveResponse = useCallback(
    (response: SaveAndExitResponse) => {
      setIsSaving(false);

      if (response.failed) {
        // Handle error case - errors remain visible until cleared
        setStatusWithTimeout({
          text:
            response.errorMessage ?? "An error occurred while saving the map",
          type: "error",
          color: "var(--error)", // Red text
          backgroundColor: "#ffebee", // Light red background
          borderColor: "var(--error)", // Red border
        });
      } else if (response.isPainting) {
        // Async painting save: start polling, status message comes from Redux
        if (rawProjectId) {
          dispatch(startMapSavePolling({ projectId: rawProjectId }));
        }
        setHasUnsavedChanges(false);
      } else {
        // Immediate success - auto-clears after 5 seconds
        setStatusWithTimeout({
          text: "Map saved successfully",
          type: "success",
          color: "var(--bintact-bright)", // Green
          backgroundColor: "#e8f5e9", // Light green background
          borderColor: "var(--bintact-bright)",
        });

        unlockProject();
        setHasUnsavedChanges(false);

        if (rawProjectId) {
          const isFirstSave = projectAreaDefined === null;
          dispatch(invalidateProjectAreaStatus());
          if (rawProjectId) {
            // Edge case: if the area is defined, but the patchLayerId is not set, we trigger baseline MSA (scenario: user just defined the project area)
            triggerMsaCalculation(rawProjectId, "project", dispatch);
            if (isFirstSave) triggerMsaCalculation(rawProjectId, "baseline", dispatch);
          }
        }
      }
    },
    [rawProjectId, unlockProject, dispatch, setStatusWithTimeout, projectAreaDefined],
  );

  // Handle editing started
  const handleEditStarted = useCallback(
    (message: EditingStartedMessage) => {
      lockProject();
      setHasUnsavedChanges(true);
      console.log("PREFIX: Edit started, time to lock the project", message);
    },
    [lockProject],
  );

  // Handle set baseline response
  const handleSetBaselineResponse = useCallback(
    (response: SetBaseLineResponse) => {
      if (response.failed) {
        const errorMsg = response.errorMessage || "Failed to set baseline";
        console.error("PREFIX: Set baseline failed:", errorMsg);

        // Handle error case - errors remain visible until cleared
        setStatusWithTimeout({
          text: errorMsg,
          type: "error",
          color: "var(--error)", // Red text
          backgroundColor: "#ffebee", // Light red background
          borderColor: "var(--error)", // Red border
        });
      } else {
        console.log("PREFIX: Baseline set successfully");

        // Show success message - auto-clears after 5 seconds
        setStatusWithTimeout({
          text: "Baseline set successfully",
          type: "success",
          color: "var(--bintact-bright)", // Green
          backgroundColor: "#e8f5e9", // Light green background
          borderColor: "var(--bintact-bright)",
        });

        if (rawProjectId) {
          triggerMsaCalculation(rawProjectId, "baseline", dispatch);
        }
      }
    },
    [dispatch, rawProjectId, setStatusWithTimeout],
  );

  // Create message handler with callbacks
  const messageHandler = createBintactMessageHandler({
    onReady: handleOpenMapRequest,
    onSaveResponse: handleSaveResponse,
    onEditingStarted: handleEditStarted,
    onSetBaseLineResponse: handleSetBaselineResponse,
  });

  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data as unknown;

      // Validate message origin
      if (!mapService.validateMessageOrigin(event.origin)) {
        return;
      }

      // Check if message is from the service (incoming messages)
      // This filters out outgoing messages we sent (source: "BINTACT")
      // and allows incoming messages from the service
      if (!mapService.isMessageFromService(data)) {
        return;
      }

      console.log("PREFIX: Received message from map:", data);

      // Handle the message
      messageHandler.handleMessage(data);

      // Track all messages for debugging (similar to original behavior)
      if (typeof data === "object" && data !== null) {
        const obj = data as Record<string, unknown>;
        const messageValue = obj.message;
        if (messageValue !== undefined) {
          setMessages((prev) => [...prev, String(messageValue)]);
        }
      } else if (typeof data === "string") {
        setMessages((prev) => [...prev, data]);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      // Clear status timeout on unmount
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, [mapService, messageHandler]);

  return {
    iframeRef,
    messages,
    requestSave: handleSaveRequest,
    requestExit: handleExitRequest,
    requestSetBaseline: handleSetBaselineRequest,
    isSaving,
    mapUrl: mapService.getMapUrl(),
    statusMessage: reduxMapSaveStatus ?? statusMessage,
    hasUnsavedChanges,
  };
};

export default useBintactMapCommunication;